import { isInvalidUserInput } from '~/utils/userInputValidation'

/**
 * When user input is invalid/junk, disables Algolia analytics for this request.
 * The search still runs; only `analytics` / `clickAnalytics` are suppressed.
 */
export const applyAlgoliaSearchAnalyticsGuard = <
  T extends Record<string, unknown>,
>(
  params: T,
  userInput?: string | null
): T => {
  const normalized = (userInput ?? '').trim()
  if (!normalized || !isInvalidUserInput(normalized)) {
    return params
  }

  if (import.meta.dev) {
    console.log(
      '[algolia] Suppressing analytics for invalid user input',
      normalized.slice(0, 80)
    )
  }

  return {
    ...params,
    analytics: false,
    clickAnalytics: false,
  }
}

interface AlgoliaMultiQueryEntry {
  query?: string
  params?: Record<string, unknown>
}

export const guardAlgoliaMultiQueryEntry = <T extends AlgoliaMultiQueryEntry>(
  entry: T
): T => {
  const userInput = String(entry.query ?? entry.params?.query ?? '')

  if (entry.params) {
    return {
      ...entry,
      params: applyAlgoliaSearchAnalyticsGuard(entry.params, userInput),
    }
  }

  return applyAlgoliaSearchAnalyticsGuard(entry, userInput)
}
